import React, { useEffect, useState } from 'react';
import axiosPrivate from 'config/axiosPrivate';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser, UserSessionSelector } from 'store/UserSession';
import { useHistory, useLocation } from 'react-router-dom';
import usePageTracking from 'utils/hooks/usePageTracking';

import { disabledInput } from 'components/shared/disabledClasses';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import { PWD_REGEX, PHONENUMBER_REGEX } from 'utils/Regex';
import { lower } from 'utils/StringUtils';
import { UserLanguageSelector } from 'components/AdminClient/UserLanguageSelector';
import { LANGUAGES } from 'utils/Constants';
import AIKeysContainer from 'components/shared/AIKeys/AIKeysContainer';

import './Profile.css';
import { useTranslation } from 'react-i18next';

const UPDATE_PROFILE_URL = '/user/external';

export default function Profile() {
  const { pathname } = useLocation();
  usePageTracking(pathname);

  let { user } = useSelector(UserSessionSelector);
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const [firstName, setFirstName] = useState('');
  const [validFirstName, setValidFirstName] = useState(false);
  const [firstNameFocus, setFirstNameFocus] = useState(false);

  const [lastName, setLastName] = useState('');
  const [validLastName, setValidLastName] = useState(false);
  const [lastNameFocus, setLastNameFocus] = useState(false);

  const [companyNameFocus, setcompanyNameFocus] = useState(false);
  const [validcompanyName, setValidcompanyName] = useState(false);
  const [companyName, setcompanyName] = useState('');

  const [phoneNumber, setPhoneNumber] = useState('');
  const [validPhone, setValidPhone] = useState(false);
  const [phoneNumberFocus, setPhoneFocus] = useState(false);

  const [pwd, setPwd] = useState('');
  const [validPwd, setValidPwd] = useState(false);
  const [pwdFocus, setPwdFocus] = useState(false);
  const [touchedPwd, setTouchedPwd] = useState(false);

  const [matchPwd, setMatchPwd] = useState('');
  const [validMatch, setValidMatch] = useState(false);
  const [matchFocus, setMatchFocus] = useState(false);
  const [touchedMatch, setTouchedMatch] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [aiProviders, setAiProviders] = useState([]);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    window.dispatchEvent(new Event('locationchange'));
    document.title = t('Typix - Mon profil');
  }, [t]);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setPhoneNumber(user.phoneNumber || '');
    setcompanyName(user.companyName || '');
    const userLanguage = LANGUAGES.find((l) => l.isoCode === user.isoLanguageCode);
    setSelectedLanguage(userLanguage || null);
  }, [user]);

  useEffect(() => { setValidFirstName(firstName && firstName.length > 0); }, [firstName]);
  useEffect(() => { setValidLastName(lastName && lastName.length > 0); }, [lastName]);
  useEffect(() => { setValidPhone(phoneNumber); }, [phoneNumber]);
  useEffect(() => { setValidcompanyName(!!companyName && companyName.trim().length > 0); }, [companyName]);

  useEffect(() => {
    let includeName =
      lower(pwd).includes(lower(firstName)) ||
      lower(pwd).includes(lower(lastName));
    setValidPwd(pwd.length === 0 || (!includeName && PWD_REGEX.test(pwd)));
    setValidMatch(pwd === matchPwd);
  }, [pwd, matchPwd, firstName, lastName]);

  const handleProviderDelete = (providerName) => {
    const updatedIaConfigs = (user.iaConfigs || []).filter(
      (c) => c.iaProvider?.toLowerCase() !== providerName
    );
    const updatedUser = { ...user, iaConfigs: updatedIaConfigs };
    dispatch(updateUser(updatedUser));
  };
  const handleSubmit = () => {
    const invalidProvider = aiProviders.find((p) => {
      if (!p.enabled) return false;
      if (p.keyChanged) return !p.apiKey || p.apiKey.trim() === '';
      return !p.hasExisting;
    });

    if (invalidProvider) {
      setErrorSuccessMsg(
        t(`Veuillez saisir votre clé API ${invalidProvider.name} ou désactiver l'utilisation de la clé IA`)
      );
      setIsSuccess(false);
      setErrorSuccess(true);
      return;
    }

    const iaConfigsRequest = aiProviders.map((p) => ({
      ...(p.id ? { id: p.id } : {}),
      iaProvider: p.name,
      iaModel: p.model,
      token: p.enabled
        ? (p.apiKey?.trim() || (p.hasExisting ? p.apiKey : null))
        : null,
      active: p.enabled,
    }));

    axiosPrivate
      .put(
        UPDATE_PROFILE_URL,
        {
          firstName,
          lastName,
          password: !pwd ? null : pwd,
          phoneNumber,
          companyName,
          role: user.role,
          isoLanguageCode: selectedLanguage?.isoCode || user.isoLanguageCode,
          iaConfigsRequest,
        },
        { params: { mail: user.mail } }
      )
      .then((res) => {
        const updatedUser = res.data;
        dispatch(updateUser(updatedUser));
        setAiProviders(updatedUser.iaConfigs || []);

        setErrorSuccessMsg(t('Votre profil a été mis à jour avec succès.'));
        setIsSuccess(true);
        setErrorSuccess(true);
      })
      .catch(() => {
        setErrorSuccessMsg(t(`Une erreur s'est produite. Veuillez vérifier votre saisie.`));
        setIsSuccess(false);
        setErrorSuccess(true);
      });
  };

  if (!user) return null;

  return (
    <div
      className="content-center"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        padding: '0 12px',
      }}
    >
      <div className="box-header-top" style={{ justifyContent: 'flex-start' }}>
        <img
          src="/Images/back-icon.png"
          alt={t('Retour')}
          className="me-3"
          style={{ cursor: 'pointer', width: 36, height: 36 }}
          onClick={history.goBack}
        />
        <h1>{t('Mon profil')}</h1>
      </div>
      <div
        className="row box-over flex-nowrap"
        style={{
          gap: '8px',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div
          className="col-12 col-sm-6 box-screen"
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <div className="box-wrapper" style={{ flex: 1, boxSizing: 'border-box' }}>
            <div className="row row-mb">
              <div className="row">
                <div className="col-12 col-md-6">
                  <div className={validFirstName ? 'label-title' : 'label-title-error'}>
                    {t('Prénom')}*
                  </div>
                  <input
                    type="text"
                    className={
                      validFirstName
                        ? 'form-control form-control-valid form-ct'
                        : 'form-control form-control-error form-ct'
                    }
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onFocus={() => setFirstNameFocus(true)}
                    onBlur={() => setFirstNameFocus(false)}
                  />
                  {firstNameFocus && !validFirstName && (
                    <p className="error-note">{t('Ce champs est obligatoire')}</p>
                  )}
                </div>
                <div className="col-12 col-md-6">
                  <div className={validLastName ? 'label-title' : 'label-title-error'}>
                    {t('Nom')}*
                  </div>
                  <input
                    type="text"
                    className={
                      validLastName
                        ? 'form-control form-control-valid form-ct'
                        : 'form-control form-control-error form-ct'
                    }
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onFocus={() => setLastNameFocus(true)}
                    onBlur={() => setLastNameFocus(false)}
                  />
                  {lastNameFocus && !validLastName && (
                    <p className="error-note">{t('Ce champs est obligatoire')}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="row row-mb">
              <div className="col-12">
                <div className="label-title">{t('E-mail')}</div>
                <input
                  type="text"
                  value={user.mail}
                  disabled
                  className="form-control form-ct"
                  style={disabledInput}
                />
              </div>
            </div>

            <div className="row row-mb">
              <div className="col-12">
                <div className={validcompanyName ? 'label-title' : 'label-title-error'}>
                  {t('Société')}*
                </div>
                <input
                  type="text"
                  className={
                    validcompanyName
                      ? 'form-control form-control-valid form-ct'
                      : 'form-control form-control-error form-ct'
                  }
                  value={companyName}
                  onChange={(e) => setcompanyName(e.target.value)}
                  onFocus={() => setcompanyNameFocus(true)}
                  onBlur={() => setcompanyNameFocus(false)}
                />
                {companyNameFocus && !validcompanyName && (
                  <p className="error-note">{t('Ce champs est obligatoire')}</p>
                )}
              </div>
            </div>

            <div className="row row-mb">
              <div className="col-12">
                <div className="label-title">{t('Téléphone')}</div>
                <input
                  type="text"
                  className="form-control form-control-valid form-ct"
                  value={phoneNumber}
                  onChange={(e) => {
                    if (PHONENUMBER_REGEX.test(e.target.value)) setPhoneNumber(e.target.value);
                  }}
                  onFocus={() => setPhoneFocus(true)}
                  onBlur={() => setPhoneFocus(false)}
                />
                {phoneNumberFocus && !validPhone && (
                  <p className="error-note">{t('Ce champs ne peut avoir que des nombres')}</p>
                )}
              </div>
            </div>

            <div className="row row-mb">
              <div className="col-12">
                <div className={touchedPwd && !validPwd ? 'label-title-error' : 'label-title'}>
                  {t('Mot de passe')}
                </div>
                <div className="input-group input-password">
                  <input
                    className={
                      touchedPwd && !validPwd
                        ? 'form-control form-control-error form-ct'
                        : 'form-control form-control-valid form-ct'
                    }
                    type={showPassword ? 'text' : 'password'}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    onFocus={() => setPwdFocus(true)}
                    onBlur={() => { setPwdFocus(false); setTouchedPwd(true); }}
                  />
                  <span
                    className={touchedPwd && !validPwd ? 'input-group-text error' : 'input-group-text'}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword
                      ? <img src="/Images/icon-eye-black.png" alt="black-eye" />
                      : <img src="/Images/icon-eye-gray.png" alt="grey-eye" />}
                  </span>
                </div>
                {touchedPwd && pwdFocus && !validPwd && (
                  <p className="error-note">
                    {t('doit comprendre au moins 8 charactères')}.<br />
                    {t('au moins une lettre minuscule et une lettre majuscule,un caractère spécial et un chiffre')}<br />
                    {t('ne doit pas inclure votre nom/prénom')}
                  </p>
                )}
              </div>
            </div>

            <div className="row row-mb">
              <div className="col-12">
                <div className={touchedMatch && !validMatch ? 'label-title-error' : 'label-title'}>
                  {t('Confirmer le mot de passe')}
                </div>
                <div className="input-group input-password">
                  <input
                    className={
                      touchedMatch && !validMatch
                        ? 'form-control form-control-error form-ct'
                        : 'form-control form-control-valid form-ct'
                    }
                    type={showPassword ? 'text' : 'password'}
                    value={matchPwd}
                    onChange={(e) => setMatchPwd(e.target.value)}
                    onFocus={() => setMatchFocus(true)}
                    onBlur={() => { setMatchFocus(false); setTouchedMatch(true); }}
                  />
                  <span
                    className={touchedMatch && !validMatch ? 'input-group-text error' : 'input-group-text'}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword
                      ? <img src="/Images/icon-eye-black.png" alt="black-eye" />
                      : <img src="/Images/icon-eye-gray.png" alt="grey-eye" />}
                  </span>
                </div>
                {touchedMatch && matchFocus && !validMatch && (
                  <p className="error-note">{t('Les deux mots de passe ne sonts pas identiques')}</p>
                )}
              </div>
            </div>

            <div className="row row-mb">
              <div className="col-12">
                <UserLanguageSelector
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={(option) => setSelectedLanguage(option)}
                />
              </div>
            </div>

            <div className="text-more">
              *{t('Avoir au moins 8 caractères avec une lettre majuscule, un chiffre, un symbole et ne pas être identique au nom.')}
            </div>
          </div>
        </div>

        <div
          className="col-12 col-sm-6 box-screen"
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <div className="box-wrapper" style={{ flex: 1, boxSizing: 'border-box' }}>
            <AIKeysContainer
              iaConfigs={user.iaConfigs || []}
              onChange={setAiProviders}
              onDelete={handleProviderDelete}
            />
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end" style={{ padding: '8px 0 4px' }}>
        {!validFirstName || !validLastName || !validPwd || !validMatch || !validcompanyName ? (
          <button className="bt-submit-disabled-y">{' ' + t('Sauvegarder') + ' '}</button>
        ) : (
          <button
            className="bt-submit-y"
            onClick={handleSubmit}
            style={{ backgroundColor: '#6458F1', color: '#fff', borderRadius: '6px' }}
          >
            {' ' + t('Sauvegarder') + ' '}
          </button>
        )}
      </div>

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => setErrorSuccess(false)}
        success={isSuccess}
        toggle={() => setErrorSuccess(!errorSuccess)}
      />
    </div>
  );
}